/**
 * A heading in the document.
 *
 * Everything this kind is: how it is made, how it is read back, what it looks
 * like on the page, and how it is edited. Nothing outside this folder needs to
 * know the kind exists.
 */

import { readBoolean, readNumber, readString } from '@/domain/json'
import { isAccent } from '@/domain/layout/colour'
import type { JsonValue } from '@/domain/json'
import { type HeadingSection } from '@/domain/section'
import HeadingBlock from '@/sections/heading/HeadingBlock.vue'
import HeadingInspector from '@/sections/heading/HeadingInspector.vue'
import type { SectionDefinition } from '@/sections/types'

/** 1 is the largest. Anything else falls back rather than producing an h9. */
function readLevel(stored: JsonValue): 1 | 2 | 3 | 4 {
  const level = readNumber(stored, 'level', 2)
  return level === 1 || level === 3 || level === 4 ? level : 2
}

/** Whether the heading is left out of the contents page. */
function isHidden(stored: JsonValue): boolean {
  return readBoolean(stored, 'hideInContents')
}

export const headingSection: SectionDefinition<'heading', HeadingSection> = {
  kind: 'heading',
  band: 'structure',

  icon: ['M3 4.5h10', 'M3 8h6'],

  create: (id, title) => ({
    id,
    kind: 'heading',
    title,
    level: 2,
    textStyleId: '',
  }),

  read: ({ id, title, stored }) => {
    // What the contents page says about the heading. Kept only where it was set,
    // so a plain heading stays plain in storage.
    const label = readString(stored, 'contentsLabel').trim()
    const detail = readString(stored, 'contentsDetail').trim()
    const colour = readString(stored, 'colour')
    return {
      id,
      kind: 'heading',
      title,
      level: readLevel(stored),
      textStyleId: readString(stored, 'textStyleId'),
      ...(label ? { contentsLabel: label } : {}),
      ...(isHidden(stored) ? { hideInContents: true as const } : {}),
      ...(isAccent(colour) ? { colour: colour.toLowerCase() } : {}),
      ...(detail ? { contentsDetail: detail } : {}),
    }
  },

  block: HeadingBlock,
  inspector: HeadingInspector,
}
